// synthetic-arabic: reviewed-no-scripture — the Arabic here is ordinary product interface wording
// invented for this application; it contains no Qur'an, hadith, translation or religious quotation.
import { messages, useMessages } from '../i18n.js';
import { useState } from 'react';
import type { ApiResult, ExploreResultDto } from '../api/client.js';

/**
 * Explore: direct search in the approved provider's own index. Nothing here is generated — every
 * result is the provider's exact source with its canonical reference, shown with a truthful label
 * (retrieved live from the provider; not a reviewer-approved pairing). Copying carries the
 * attribution with the text, so a shared verse never travels unattributed.
 */
const T = messages({
  ar: {
    rateLimited: 'محاولات كثيرة. انتظر قليلًا ثم ابحث مجددًا.',
    unavailable: 'تعذّر الوصول إلى المصدر الآن. حاول بعد قليل.',
    copyFailed: 'تعذّر النسخ في هذا المتصفح.',
    region: 'استكشاف المصادر',
    title: 'استكشاف المصادر',
    close: 'إغلاق',
    note: 'بحث مباشر في نص القرآن الكريم من المزوّد المعتمد. النتائج تُعرض كما وردت من المصدر، بمرجعها — وليست فتوى ولا اعتمادًا شرعيًا.',
    label: 'كلمة البحث',
    prompt: 'ابحث بكلمة… مثل: الصبر',
    search: 'بحث',
    searching: 'أبحث في المصدر…',
    none: 'لا نتائج لهذه الكلمة.',
    found: (shown: number, total: number) => `النتائج: ${String(shown)} من ${String(total)}`,
    copied: 'نُسخ ✓',
    copy: 'نسخ',
  },
  en: {
    rateLimited: 'Too many attempts. Wait a moment, then search again.',
    unavailable: 'The source cannot be reached right now. Try again shortly.',
    copyFailed: 'Copying is not available in this browser.',
    region: 'Explore the sources',
    title: 'Explore the sources',
    close: 'Close',
    note: 'A live search of the Qur’an text from the approved provider. Results appear exactly as the source gives them, with their reference — and are neither a ruling nor a religious approval.',
    label: 'Search term',
    prompt: 'Search a word…',
    search: 'Search',
    searching: 'Searching the source…',
    none: 'No results for that word.',
    found: (shown: number, total: number) => `Results: ${String(shown)} of ${String(total)}`,
    copied: 'Copied ✓',
    copy: 'Copy',
  },
});

export function ExplorePanel({
  search,
  onClose,
}: {
  readonly search: (
    query: string,
  ) => Promise<
    ApiResult<{ readonly totalFound: number; readonly results: readonly ExploreResultDto[] }>
  >;
  readonly onClose: () => void;
}): React.JSX.Element {
  const t = useMessages(T);
  const [query, setQuery] = useState('');
  const [phase, setPhase] = useState<'idle' | 'searching' | 'done'>('idle');
  const [results, setResults] = useState<readonly ExploreResultDto[]>([]);
  const [totalFound, setTotalFound] = useState(0);
  const [failure, setFailure] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const run = async (): Promise<void> => {
    const trimmed = query.trim();
    if (trimmed.length === 0 || phase === 'searching') return;
    setPhase('searching');
    setFailure(null);
    const outcome = await search(trimmed);
    if (!outcome.ok) {
      setFailure(outcome.code === 'rate_limited' ? t.rateLimited : t.unavailable);
      setResults([]);
      setPhase('done');
      return;
    }
    setResults(outcome.value.results);
    setTotalFound(outcome.value.totalFound);
    setPhase('done');
  };

  const copy = async (result: ExploreResultDto): Promise<void> => {
    // The attribution travels with the words, always.
    const block = `${result.text}\n[${result.reference}] — ${result.attribution}`;
    try {
      await globalThis.navigator.clipboard.writeText(block);
      setCopied(result.reference);
      setTimeout(() => {
        setCopied(null);
      }, 1500);
    } catch {
      setFailure(t.copyFailed);
    }
  };

  return (
    <section aria-label={t.region} className="explore">
      <div className="explore-head">
        <h2>{t.title}</h2>
        <button type="button" className="icon-button" aria-label={t.close} onClick={onClose}>
          ✕
        </button>
      </div>
      <p className="explore-note">{t.note}</p>
      <div className="explore-search">
        <label htmlFor="explore-q" className="visually-hidden">
          {t.label}
        </label>
        <input
          id="explore-q"
          dir="auto"
          placeholder={t.prompt}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') void run();
          }}
        />
        <button type="button" disabled={phase === 'searching'} onClick={() => void run()}>
          {t.search}
        </button>
      </div>
      {phase === 'searching' && (
        <p role="status" className="explore-status">
          {t.searching}
        </p>
      )}
      {failure !== null && <p role="alert">{failure}</p>}
      {phase === 'done' && failure === null && results.length === 0 && (
        <p className="explore-status">{t.none}</p>
      )}
      {results.length > 0 && (
        <>
          <p className="explore-status">{t.found(results.length, totalFound)}</p>
          <ol className="explore-results">
            {results.map((result) => (
              <li key={result.reference} className="explore-result">
                <p className="explore-text" dir="rtl" lang="ar">
                  {result.text}
                </p>
                <p className="explore-meta">
                  <span className="explore-ref">{result.reference}</span>
                  <span className="explore-attr">{result.attribution}</span>
                  <button type="button" onClick={() => void copy(result)}>
                    {copied === result.reference ? t.copied : t.copy}
                  </button>
                </p>
              </li>
            ))}
          </ol>
        </>
      )}
    </section>
  );
}
